import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { forkJoin, of, Subscription } from "rxjs";
import { catchError, filter, switchMap } from "rxjs/operators";

import {
  ChangeRequestDbRow,
  JswVmdService,
} from "src/app/_app-core/services/jsw-vmd/jsw-vmd.service";
import { Config } from "src/app/_store/config";

interface StgColumnEntry {
  key: string;
  isArray: boolean;
  value: unknown;
}

@Component({
  selector: "app-change-requests",
  templateUrl: "./change-requests.component.html",
  styleUrls: ["./change-requests.component.scss"],
})
export class ChangeRequestsComponent implements OnInit, OnDestroy {
  records: ChangeRequestDbRow[] = [];
  loading = false;
  isRefreshingDetails = false;
  errorMsg = "";
  isCondensed = false;
  page = 1;
  pageSize = Config.pageSize;
  collectionSize = 0;
  /** True when JSW in-process API returns no active requests (`apiDataArray.length === 0`). */
  createChangeRequestAllowed = true;

  selectedUpdatedFields: Record<string, unknown> | null = null;
  selectedUploadedDocumentNames: string[] = [];

  @ViewChild("stgColumnModal", { static: true })
  stgColumnModal!: TemplateRef<unknown>;

  readonly jswAuthFailed$ = this.jsw.jswAuthFailed$;

  private authFailedSub?: Subscription;
  private routerSub?: Subscription;
  private lastAuthFailed = false;

  constructor(
    private jsw: JswVmdService,
    private router: Router,
    private modal: NgbModal,
  ) {}

  ngOnInit(): void {
    // Trigger an initial load. When JSW auth has already failed (e.g. login
    // flagged it), `load()` will short-circuit and the banner will be shown.
    this.load();
    // When the user re-authenticates successfully the flag flips back to
    // false; that's our cue to repopulate the page.
    this.authFailedSub = this.jswAuthFailed$.subscribe((failed) => {
      if (this.lastAuthFailed && !failed) {
        this.load();
      }
      this.lastAuthFailed = failed;
    });
    this.routerSub = this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      )
      .subscribe((e) => {
        if (this.isChangeRequestsListRoute(e.urlAfterRedirects || e.url)) {
          this.load();
        }
      });
  }

  ngOnDestroy(): void {
    this.authFailedSub?.unsubscribe();
    this.routerSub?.unsubscribe();
  }

  /** True for `/change-requests` list only (not new or resubmit child routes). */
  private isChangeRequestsListRoute(url: string): boolean {
    const path = (url || "").split("?")[0].split("#").pop() || "";
    if (!path.includes("change-requests")) {
      return false;
    }
    return (
      !path.includes("change-requests/new") &&
      !path.includes("change-requests/resubmit")
    );
  }

  get canCreateChangeRequest(): boolean {
    return (
      !this.loading &&
      !this.jsw.isJswAuthFailed &&
      this.createChangeRequestAllowed
    );
  }

  get changeRequestCreateButtonTitle(): string {
    if (this.loading) {
      return "Loading…";
    }
    if (this.jsw.isJswAuthFailed) {
      return "Re-authenticate with JSW to create a change request.";
    }
    if (this.createChangeRequestAllowed) {
      return "Create a change request";
    }
    return "A change request is already in process with the JSW system.";
  }

  editRequestButtonTitle(row: ChangeRequestDbRow): string {
    if (this.loading) {
      return "Loading…";
    }
    if (!this.canCreateChangeRequest) {
      return "A change request is already in process";
    }
    return "Edit this request";
  }

  isRequestRejected(row: ChangeRequestDbRow): boolean {
    return (
      String((row && row.status) || "").trim() ===
      JswVmdService.CR_STATUS_REQUEST_REJECTED
    );
  }

  editRejectedRequest(row: ChangeRequestDbRow): void {
    if (!this.canCreateChangeRequest) {
      return;
    }
    const id = (row && row._id) || "";
    if (!id) {
      return;
    }
    const navState = {
      state: {
        updatedFields:
          row.updatedFields && typeof row.updatedFields === "object"
            ? row.updatedFields
            : {},
      },
    };
    const vendorCode = (localStorage.getItem("vendorCode") || "").trim();
    if (!vendorCode) {
      this.router.navigate(["/change-requests/resubmit", id], navState);
      return;
    }
    this.isRefreshingDetails = true;
    this.jsw.fetchAndStoreForVendor(vendorCode).subscribe(() => {
      this.isRefreshingDetails = false;
      this.router.navigate(["/change-requests/resubmit", id], navState);
    });
  }

  load(): void {
    this.errorMsg = "";
    const vendorCode = (localStorage.getItem("vendorCode") || "").trim();
    if (!vendorCode) {
      this.records = [];
      this.collectionSize = 0;
      this.loading = false;
      this.errorMsg = "Vendor code is missing. Please log in again.";
      return;
    }

    // Suppress every JSW-backed and downstream backend call while auth is
    // broken; the banner alone is shown until the user re-authenticates.
    if (this.jsw.isJswAuthFailed) {
      this.records = [];
      this.collectionSize = 0;
      this.loading = false;
      return;
    }
    this.loading = true;

    this.jsw
      .refreshChangeRequestStatuses(vendorCode)
      .pipe(
        switchMap(() =>
          forkJoin([
            this.jsw.listChangeRequestsFromDb(vendorCode, this.page, this.pageSize),
            this.jsw.fetchChangeRequests(vendorCode).pipe(
              catchError(() => of([])),
            ),
            this.jsw.fetchAllPendingChangeRequestRows(vendorCode).pipe(
              catchError(() => of([] as Array<{ _id: string; status: string }>)),
            ),
          ]),
        ),
        catchError((err) => {
          console.error("Failed to load change requests from DB", err);
          return of(null);
        }),
      )
      .subscribe((result) => {
        const payload = result ? result[0] : null;
        const jswRows = result ? result[1] : [];
        const pendingRows = result ? result[2] : [];
        if (payload) {
          this.records = payload.list;
          this.collectionSize = payload.noOfRecords;
          const hasBlockingDbStatus = pendingRows.some((r) =>
            JswVmdService.CR_BLOCKING_DB_STATUSES.has(
              ((r && r.status) || "").trim(),
            ),
          );
          this.createChangeRequestAllowed =
            jswRows.length === 0 && !hasBlockingDbStatus;
        } else {
          this.records = [];
          this.collectionSize = 0;
          this.createChangeRequestAllowed = true;
        }
        // If a 401 flipped jswAuthFailed mid-load, the banner now owns the
        // screen; clear any error text we may have queued so we don't render
        // both the banner and a redundant inline error.
        if (!this.jsw.isJswAuthFailed && result === null) {
          this.errorMsg =
            "Unable to load saved change requests. Please try again.";
        }
        this.loading = false;
      });
  }

  onListPageChange(): void {
    this.load();
  }

  onListPageSizeChange(): void {
    this.page = 1;
    this.load();
  }

  onCreateChangeRequest(): void {
    if (!this.canCreateChangeRequest) {
      return;
    }
    const vendorCode = (localStorage.getItem("vendorCode") || "").trim();
    if (!vendorCode) {
      this.router.navigate(["/change-requests/new"]);
      return;
    }
    this.isRefreshingDetails = true;
    this.jsw.fetchAndStoreForVendor(vendorCode).subscribe(() => {
      this.isRefreshingDetails = false;
      this.router.navigate(["/change-requests/new"]);
    });
  }

  countOfChangedFields(row: ChangeRequestDbRow): number {
    const updated = row && row.updatedFields;
    if (!updated || typeof updated !== "object") {
      return 0;
    }
    return Object.keys(updated).length;
  }

  hasStatusComment(row: ChangeRequestDbRow): boolean {
    const c = row && row.statusComments;
    return typeof c === "string" && c.trim() !== "";
  }

  statusCommentTooltip(row: ChangeRequestDbRow): string {
    return String((row && row.statusComments) || "").trim();
  }

  openStgColumnDetails(row: ChangeRequestDbRow): void {
    this.selectedUpdatedFields = (row && row.updatedFields) || {};
    const raw = row && row.uploadedDocumentNames;
    this.selectedUploadedDocumentNames = Array.isArray(raw)
      ? raw.map((n) => String(n))
      : [];
    this.modal.open(this.stgColumnModal, { centered: true, size: "md" });
  }

  stgColumnEntries(): StgColumnEntry[] {
    const updated = this.selectedUpdatedFields || {};
    return Object.keys(updated).map((key) => ({
      key,
      isArray: Array.isArray(updated[key]),
      value: updated[key],
    }));
  }

  bankRowEntries(value: unknown): Array<{ key: string; value: unknown }> {
    if (!value || typeof value !== "object") {
      return [];
    }
    const obj = value as Record<string, unknown>;
    return Object.keys(obj).map((key) => ({ key, value: obj[key] }));
  }

  asArray(value: unknown): unknown[] {
    return Array.isArray(value) ? value : [];
  }
}
